/**
 * The social links editor and the company website field.
 *
 * One component for all four places these are collected — signup, My Profile,
 * the admin add-member drawer and the admin edit drawer — for the same reason
 * ProfileContactInputs exists: the same field implemented four times becomes
 * four sets of rules, and a member's Instagram link should not depend on which
 * screen they happened to save it from.
 *
 * The conversions live in @shared/socialLinks and are shared with the server, so
 * what a form accepts is exactly what the API will store.
 *
 * Rows are held as what the member typed and converted on save. Normalizing on
 * every keystroke would rewrite the field mid-word — the same reason the
 * LinkedIn control tidies on blur rather than as you type.
 */
import { type CSSProperties } from "react";
import { Globe, Link2, Plus, X } from "lucide-react";
import {
  MAX_SOCIAL_LINKS,
  normalizeSocialLink,
  SOCIAL_PLATFORMS,
  socialHandleFromUrl,
  socialPlatform,
  type SocialLink,
  type SocialPlatformId,
} from "@shared/socialLinks";

/** A row being edited: the chosen platform and the raw text in its box. */
export type SocialLinkDraft = {
  platform: SocialPlatformId;
  value: string;
};

const baseInputStyle: CSSProperties = {
  width: "100%",
  minHeight: 42,
  padding: ".6rem .75rem",
  borderRadius: ".625rem",
  border: "1px solid rgba(255,255,255,.1)",
  background: "rgba(255,255,255,.04)",
  color: "#F8FAFC",
  fontFamily: "'Inter', sans-serif",
  fontSize: ".85rem",
  outline: "none",
  boxSizing: "border-box",
};

const hintStyle: CSSProperties = {
  margin: ".35rem 0 0",
  color: "#64748B",
  fontFamily: "'Inter', sans-serif",
  fontSize: "var(--chi-body-compact)",
  lineHeight: 1.45,
};

/**
 * What entering a link means, said where it is entered.
 *
 * There is no separate "show my socials" switch by design — a link a member
 * saves is shown to paid members in the directory. That makes the act of typing
 * it the act of consenting to publish it, so the form has to say so rather than
 * leaving someone to discover it in the directory afterwards.
 */
export const SOCIAL_LINKS_DISCLOSURE =
  "Optional. Any link you add here is shown to paid members in the member directory — leave a row out if you would rather it stayed private.";

/** Stored links back into editable rows. */
export function socialLinkDrafts(links: readonly SocialLink[]): SocialLinkDraft[] {
  return links.map(link => ({ platform: link.platform, value: socialHandleFromUrl(link) }));
}

/**
 * The first problem in the set, or null.
 *
 * Returned rather than thrown so a form can show the message beside the field
 * and still decide for itself whether to block the save.
 */
export function socialLinkDraftError(drafts: readonly SocialLinkDraft[]): string | null {
  for (const draft of drafts) {
    try {
      normalizeSocialLink(draft.platform, draft.value);
    } catch (error) {
      return error instanceof Error ? error.message : "Check this link.";
    }
  }
  return drafts.length > MAX_SOCIAL_LINKS ? `Save at most ${MAX_SOCIAL_LINKS} social links.` : null;
}

export default function SocialLinksInput({
  drafts,
  onChange,
  disabled,
  idPrefix = "social-link",
}: {
  drafts: SocialLinkDraft[];
  onChange: (next: SocialLinkDraft[]) => void;
  disabled?: boolean;
  idPrefix?: string;
}) {
  const error = socialLinkDraftError(drafts);

  const update = (index: number, patch: Partial<SocialLinkDraft>) =>
    onChange(drafts.map((draft, position) => (position === index ? { ...draft, ...patch } : draft)));

  return (
    <div style={{ display: "grid", gap: ".5rem" }}>
      {drafts.map((draft, index) => {
        const definition = socialPlatform(draft.platform);
        return (
          <div key={index} style={{ display: "flex", alignItems: "stretch", gap: ".4rem" }}>
            <select
              aria-label={`Platform for link ${index + 1}`}
              value={draft.platform}
              disabled={disabled}
              onChange={event => update(index, { platform: event.target.value as SocialPlatformId })}
              style={{ ...baseInputStyle, width: "auto", flexShrink: 0, cursor: disabled ? "default" : "pointer" }}
            >
              {SOCIAL_PLATFORMS.map(platform => (
                <option key={platform.id} value={platform.id} style={{ background: "#0D1220" }}>
                  {platform.label}
                </option>
              ))}
            </select>
            <input
              id={`${idPrefix}-${index}`}
              aria-label={`${definition?.label ?? "Social"} link`}
              value={draft.value}
              disabled={disabled}
              onChange={event => update(index, { value: event.target.value })}
              placeholder={definition?.prefix ? "Enter your username or handle" : "Enter the full web address"}
              style={baseInputStyle}
            />
            <button
              type="button"
              aria-label={`Remove link ${index + 1}`}
              disabled={disabled}
              onClick={() => onChange(drafts.filter((_, position) => position !== index))}
              style={{ flexShrink: 0, width: 42, display: "grid", placeItems: "center", borderRadius: ".625rem", border: "1px solid rgba(255,255,255,.1)", background: "rgba(255,255,255,.03)", color: "#94A3B8", cursor: disabled ? "default" : "pointer" }}
            >
              <X size={15} />
            </button>
          </div>
        );
      })}

      {drafts.length < MAX_SOCIAL_LINKS && (
        <button
          type="button"
          disabled={disabled}
          onClick={() => onChange([...drafts, { platform: "x", value: "" }])}
          style={{ justifySelf: "start", display: "inline-flex", alignItems: "center", gap: ".35rem", minHeight: 38, padding: "0 .8rem", borderRadius: ".625rem", border: "1px dashed rgba(255,255,255,.18)", background: "transparent", color: "#9DEFFF", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700, cursor: disabled ? "default" : "pointer" }}
        >
          <Plus size={14} /> {drafts.length ? "Add another link" : "Add a social link"}
        </button>
      )}

      <p style={hintStyle}>
        <Link2 size={12} style={{ display: "inline", verticalAlign: "-1px", marginRight: ".25rem" }} />
        {SOCIAL_LINKS_DISCLOSURE}
      </p>
      {error && (
        <p role="alert" style={{ ...hintStyle, color: "#FCA5A5" }}>
          {error}
        </p>
      )}
    </div>
  );
}

/** The company or organization website, on the same rules as the links above. */
export function CompanyWebsiteInput({
  value,
  onChange,
  id = "company-website",
  disabled,
}: {
  value: string;
  onChange: (next: string) => void;
  id?: string;
  disabled?: boolean;
}) {
  return (
    <div style={{ display: "flex", alignItems: "stretch", border: "1px solid rgba(255,255,255,.1)", borderRadius: ".625rem", overflow: "hidden", background: "rgba(255,255,255,.04)" }}>
      <span
        aria-hidden="true"
        style={{ display: "inline-flex", alignItems: "center", gap: ".35rem", padding: "0 .72rem", borderRight: "1px solid rgba(255,255,255,.09)", color: "#7E8CA0", background: "rgba(255,255,255,.025)", fontFamily: "'Inter', sans-serif", fontSize: ".76rem", whiteSpace: "nowrap" }}
      >
        <Globe size={14} /> https://
      </span>
      <input
        id={id}
        aria-label="Company website"
        autoComplete="url"
        disabled={disabled}
        value={value}
        onChange={event => onChange(event.target.value)}
        placeholder="Enter your company website"
        style={{ ...baseInputStyle, border: 0, borderRadius: 0, background: "transparent", minWidth: 0 }}
      />
    </div>
  );
}
